import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	ButtonBase,
	Chip,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stepper,
	Step,
	StepLabel,
	Switch,
	TextField,
	Typography,
} from "@material-ui/core";
import { Field } from "formik";
import { CheckCircleOutline, ErrorOutline, ExpandMore } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { i18n } from "../../translate/i18n";
import { getAiUsageEstimate } from "./userCreationFlow.mjs";
import AiOperationalSkills from "./AiOperationalSkills";

const useStyles = makeStyles(theme => ({
	wizard: { width: "100%" },
	header: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(1),
		[theme.breakpoints.down("xs")]: { flexDirection: "column" },
	},
	headerTitle: { fontWeight: 700 },
	muted: { color: theme.palette.text.secondary, lineHeight: 1.5 },
	advancedButton: {
		padding: theme.spacing(0.5, 1),
		textTransform: "none",
		borderRadius: 999,
		whiteSpace: "nowrap",
	},
	stepper: {
		padding: theme.spacing(1, 0, 2),
		background: "transparent",
		[theme.breakpoints.down("xs")]: { display: "none" },
	},
	stepPanel: {
		borderTop: `1px solid ${theme.palette.divider}`,
		paddingTop: theme.spacing(2),
	},
	stepHeading: {
		fontWeight: 700,
		marginBottom: theme.spacing(0.5),
		outline: "none",
	},
	stepHelp: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2) },
	choiceGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
		gap: theme.spacing(1.5),
	},
	choice: {
		display: "block",
		width: "100%",
		padding: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
		textAlign: "left",
		transition: "border-color 120ms ease, background-color 120ms ease",
		"&:hover": { borderColor: theme.palette.primary.main },
	},
	choiceSelected: {
		borderColor: theme.palette.primary.main,
		backgroundColor: theme.palette.action.selected,
	},
	choiceTitleRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(0.5),
	},
	choiceTitle: { fontWeight: 700 },
	capabilityRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: theme.spacing(1.5),
	},
	chipRow: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(0.75),
		marginTop: theme.spacing(1),
	},
	fieldsGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
		gap: theme.spacing(1.5, 2),
		[theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
	},
	fullField: { gridColumn: "1 / -1" },
	section: { marginTop: theme.spacing(2.5) },
	sectionTitle: { fontWeight: 700, marginBottom: theme.spacing(0.5) },
	optional: {
		marginTop: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
		boxShadow: "none",
		"&:before": { display: "none" },
	},
	optionalDetails: { display: "block", padding: theme.spacing(0, 2, 2) },
	readinessGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
		gap: theme.spacing(1),
		[theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
	},
	readinessItem: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(1),
		padding: theme.spacing(1.5),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 10,
	},
	readyIcon: { color: theme.palette.success.main, fontSize: 20, marginTop: 1 },
	missingIcon: { color: theme.palette.warning.main, fontSize: 20, marginTop: 1 },
	reviewCard: {
		marginTop: theme.spacing(2),
		padding: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
	},
	reviewLine: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: theme.spacing(2),
		padding: theme.spacing(1, 0),
		borderBottom: `1px solid ${theme.palette.divider}`,
		"&:last-child": { borderBottom: "none" },
	},
	reviewValue: { textAlign: "right", fontWeight: 600 },
	testList: {
		margin: theme.spacing(1, 0, 0),
		paddingLeft: theme.spacing(2.5),
		color: theme.palette.text.secondary,
		"& li + li": { marginTop: theme.spacing(0.75) },
	},
	loading: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(3, 0),
	},
	usageCard: {
		marginTop: theme.spacing(2),
		padding: theme.spacing(1.5, 2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 10,
		backgroundColor: theme.palette.action.hover,
	},
	simulatorActions: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		marginTop: theme.spacing(1),
		[theme.breakpoints.down("xs")]: { alignItems: "stretch", flexDirection: "column" },
	},
	simulatorAnswer: {
		marginTop: theme.spacing(1.5),
		padding: theme.spacing(1.5),
		borderLeft: `3px solid ${theme.palette.primary.main}`,
		backgroundColor: theme.palette.action.hover,
		whiteSpace: "pre-wrap",
	},
}));

const optionSets = {
	intelligenceProfile: ["essential", "balanced", "advanced"],
	tone: ["welcoming", "professional", "casual", "technical"],
	responseLength: ["short", "balanced", "detailed"],
	autonomyLevel: ["answer_only", "suggest_actions", "confirm_actions", "automatic"],
};

const AiAgentWizard = ({
	values,
	setFieldValue,
	touched,
	errors,
	queues,
	activeStep,
	selectedQueueIds,
	onQueueIdsChange,
	whatsappId,
	onWhatsappIdChange,
	whatsApps,
	loadingWhatsApps,
	readiness,
	userId,
	onSwitchSetupMode,
}) => {
	const classes = useStyles();
	const wizardRef = useRef(null);
	const headingRef = useRef(null);
	const previousStepRef = useRef(activeStep);
	const [templates, setTemplates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [simulatorMessage, setSimulatorMessage] = useState("");
	const [simulatorAnswer, setSimulatorAnswer] = useState("");
	const [simulating, setSimulating] = useState(false);

	const metadata = values.aiAgentProfile?.metadata || {};
	const businessData = metadata.businessData || {};
	const selectedCapabilityKeys = Array.isArray(metadata.selectedCapabilityKeys)
		? metadata.selectedCapabilityKeys
		: [];
	const selectedTemplate = useMemo(
		() => templates.find(template => template.key === metadata.templateKey),
		[templates, metadata.templateKey]
	);

	useEffect(() => {
		let mounted = true;
		const loadTemplates = async () => {
			try {
				const { data } = await api.get("/users/ai-agent/templates");
				if (mounted) setTemplates(Array.isArray(data?.templates) ? data.templates : []);
			} catch (err) {
				toastError(err);
			} finally {
				if (mounted) setLoading(false);
			}
		};
		loadTemplates();
		return () => { mounted = false; };
	}, []);

	useEffect(() => {
		if (!selectedTemplate || metadata.capabilitiesInitialized) return;
		setFieldValue(
			"aiAgentProfile.metadata.selectedCapabilityKeys",
			selectedTemplate.capabilities
				.filter(capability => capability.defaultEnabled)
				.map(capability => capability.key),
			false
		);
		setFieldValue("aiAgentProfile.metadata.capabilitiesInitialized", true, false);
	}, [metadata.capabilitiesInitialized, selectedTemplate, setFieldValue]);

	useEffect(() => {
		if (previousStepRef.current !== activeStep) {
			wizardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
			headingRef.current?.focus({ preventScroll: true });
			previousStepRef.current = activeStep;
		}
	}, [activeStep]);

	const updateMetadata = (key, value) => {
		setFieldValue(`aiAgentProfile.metadata.${key}`, value);
		setFieldValue("aiAgentProfile.metadata.wizardCompleted", false, false);
	};

	const chooseTemplate = template => {
		setFieldValue("aiAgentProfile.metadata.templateKey", template.key);
		setFieldValue("aiAgentProfile.metadata.templateVersion", template.version);
		setFieldValue(
			"aiAgentProfile.metadata.selectedCapabilityKeys",
			template.capabilities
				.filter(capability => capability.defaultEnabled)
				.map(capability => capability.key)
		);
		setFieldValue("aiAgentProfile.metadata.capabilitiesInitialized", true, false);
		setFieldValue("aiAgentProfile.metadata.wizardCompleted", false, false);
	};

	const toggleCapability = capabilityKey => {
		const next = selectedCapabilityKeys.includes(capabilityKey)
			? selectedCapabilityKeys.filter(key => key !== capabilityKey)
			: [...selectedCapabilityKeys, capabilityKey];
		updateMetadata("selectedCapabilityKeys", next);
	};

	const title = key => i18n.t(`userModal.aiAgent.wizard.templates.${key}`);
	const capabilityTitle = key => i18n.t(`userModal.aiAgent.wizard.capabilities.${key}`);
	const selectedWhatsapp = whatsApps.find(item => item.id === whatsappId);
	const selectedQueueNames = queues
		.filter(queue => selectedQueueIds.includes(queue.id))
		.map(queue => queue.name)
		.join(", ");
	const capabilityNames = selectedTemplate?.capabilities
		.filter(capability => selectedCapabilityKeys.includes(capability.key))
		.map(capability => capabilityTitle(capability.titleKey))
		.join(", ");
	const usageEstimate = getAiUsageEstimate(metadata.intelligenceProfile);
	const requiresScheduling = selectedCapabilityKeys.some(key =>
		["scheduling", "appointment_management", "demo_scheduling"].includes(key)
	);
	const requiresAdvancedOperations = selectedCapabilityKeys.some(key =>
		["advanced_operations", "advanced_integrations"].includes(key)
	);
	const readinessEntries = Object.entries(readiness.checks).filter(([key]) => {
		if (key === "scheduling") return requiresScheduling;
		if (key === "operations") return requiresAdvancedOperations;
		return true;
	});

	const runSimulation = async () => {
		if (!userId || !String(simulatorMessage || "").trim()) return;
		setSimulating(true);
		setSimulatorAnswer("");
		try {
			const { data } = await api.post(`/users/${userId}/ai-agent/simulate`, {
				message: simulatorMessage,
			});
			setSimulatorAnswer(String(data?.answer || ""));
		} catch (err) {
			toastError(err);
		} finally {
			setSimulating(false);
		}
	};

	const renderOptions = field => (
		<div className={classes.choiceGrid}>
			{optionSets[field].map(option => (
				<ButtonBase
					key={option}
					className={`${classes.choice} ${metadata[field] === option ? classes.choiceSelected : ""}`}
					onClick={() => updateMetadata(field, option)}
				>
					<div className={classes.choiceTitleRow}>
						<Typography className={classes.choiceTitle}>{i18n.t(`userModal.aiAgent.wizard.behavior.${field}.${option}`)}</Typography>
						{metadata[field] === option && <CheckCircleOutline color="primary" />}
					</div>
				</ButtonBase>
			))}
		</div>
	);

	if (loading) {
		return (
			<div className={classes.loading}>
				<CircularProgress size={22} />
				<Typography variant="body2">{i18n.t("userModal.aiAgent.wizard.loading")}</Typography>
			</div>
		);
	}

	return (
		<div className={classes.wizard} ref={wizardRef} data-testid="ai-agent-wizard">
			<div className={classes.header}>
				<div>
					<Typography variant="subtitle1" className={classes.headerTitle}>{i18n.t("userModal.aiAgent.wizard.title")}</Typography>
					<Typography variant="body2" className={classes.muted}>{i18n.t("userModal.aiAgent.wizard.help")}</Typography>
				</div>
				<Button type="button" color="primary" size="small" className={classes.advancedButton} onClick={() => onSwitchSetupMode("advanced")}>
					{i18n.t("userModal.aiAgent.wizard.advancedMode")}
				</Button>
			</div>

			<Stepper activeStep={activeStep} alternativeLabel className={classes.stepper}>
				{["objective", "knowledge", "capabilities", "behavior", "review"].map(step => (
					<Step key={step}><StepLabel>{i18n.t(`userModal.aiAgent.wizard.steps.${step}`)}</StepLabel></Step>
				))}
			</Stepper>

			<div className={classes.stepPanel}>
				{activeStep === 0 && (
					<>
						<Typography ref={headingRef} tabIndex={-1} variant="subtitle1" className={classes.stepHeading}>{i18n.t("userModal.aiAgent.wizard.objectiveTitle")}</Typography>
						<Typography variant="body2" className={classes.stepHelp}>{i18n.t("userModal.aiAgent.wizard.objectiveHelp")}</Typography>
						<div className={classes.fieldsGrid}>
							<TextField name="name" value={values.name || ""} onChange={event => setFieldValue("name", event.target.value)} label={i18n.t("userModal.aiAgent.wizard.agentName")} autoFocus error={touched.name && Boolean(errors.name)} helperText={(touched.name && errors.name) || i18n.t("userModal.aiAgent.wizard.agentNameHelp")} variant="outlined" margin="dense" fullWidth />
							<FormControl variant="outlined" margin="dense" fullWidth>
								<InputLabel>{i18n.t("userModal.form.language")}</InputLabel>
								<Field as={Select} name="language" label={i18n.t("userModal.form.language")}>
									<MenuItem value="pt-BR">{i18n.t("signup.form.languageOptions.pt")}</MenuItem>
									<MenuItem value="en">{i18n.t("signup.form.languageOptions.en")}</MenuItem>
									<MenuItem value="es">{i18n.t("signup.form.languageOptions.es")}</MenuItem>
								</Field>
							</FormControl>
						</div>
						<div className={classes.section}>
							<Typography variant="subtitle2" className={classes.sectionTitle}>{i18n.t("userModal.aiAgent.wizard.segmentTitle")}</Typography>
							<Typography variant="body2" className={classes.stepHelp}>{i18n.t("userModal.aiAgent.wizard.segmentHelp")}</Typography>
							<div className={classes.choiceGrid}>
								{templates.map(template => (
									<ButtonBase key={template.key} className={`${classes.choice} ${metadata.templateKey === template.key ? classes.choiceSelected : ""}`} onClick={() => chooseTemplate(template)}>
										<div className={classes.choiceTitleRow}>
											<Typography className={classes.choiceTitle}>{title(template.titleKey)}</Typography>
											{metadata.templateKey === template.key && <CheckCircleOutline color="primary" />}
										</div>
										<Typography variant="body2" className={classes.muted}>{title(template.descriptionKey)}</Typography>
									</ButtonBase>
								))}
							</div>
						</div>
					</>
				)}

				{activeStep === 1 && (
					<>
						<Typography ref={headingRef} tabIndex={-1} variant="subtitle1" className={classes.stepHeading}>{i18n.t("userModal.aiAgent.wizard.businessTitle")}</Typography>
						<Typography variant="body2" className={classes.stepHelp}>{i18n.t("userModal.aiAgent.wizard.businessHelp")}</Typography>
						<div className={classes.fieldsGrid}>
							<TextField
								name="aiAgentProfile.metadata.businessData.organizationName"
								value={businessData.organizationName || ""}
								onChange={event => setFieldValue("aiAgentProfile.metadata.businessData.organizationName", event.target.value)}
								label={i18n.t("userModal.aiAgent.wizard.fields.organizationName")}
								required
								error={Boolean(touched.aiAgentProfile?.metadata?.businessData?.organizationName && !String(businessData.organizationName || "").trim())}
								helperText={touched.aiAgentProfile?.metadata?.businessData?.organizationName && !String(businessData.organizationName || "").trim() ? i18n.t("userModal.validation.required") : ""}
								variant="outlined"
								margin="dense"
								fullWidth
							/>
							<div className={classes.fullField}><Field as={TextField} name="aiAgentProfile.metadata.businessData.summary" label={i18n.t("userModal.aiAgent.wizard.fields.summary")} helperText={i18n.t("userModal.aiAgent.wizard.fields.summaryHelp")} multiline minRows={3} variant="outlined" margin="dense" fullWidth /></div>
							<div className={classes.fullField}><Field as={TextField} name="aiAgentProfile.metadata.businessData.services" label={i18n.t("userModal.aiAgent.wizard.fields.services")} multiline minRows={3} variant="outlined" margin="dense" fullWidth /></div>
						</div>
						<Accordion className={classes.optional}>
							<AccordionSummary expandIcon={<ExpandMore />}>
								<div><Typography className={classes.choiceTitle}>{i18n.t("userModal.aiAgent.wizard.moreKnowledgeTitle")}</Typography><Typography variant="body2" className={classes.muted}>{i18n.t("userModal.aiAgent.wizard.moreKnowledgeHelp")}</Typography></div>
							</AccordionSummary>
							<AccordionDetails className={classes.optionalDetails}>
								<div className={classes.fieldsGrid}>
									<Field as={TextField} name="aiAgentProfile.metadata.businessData.businessHours" label={i18n.t("userModal.aiAgent.wizard.fields.businessHours")} variant="outlined" margin="dense" fullWidth />
									<Field as={TextField} name="aiAgentProfile.metadata.businessData.address" label={i18n.t("userModal.aiAgent.wizard.fields.address")} variant="outlined" margin="dense" fullWidth />
									<Field as={TextField} name="aiAgentProfile.metadata.businessData.schedulingUrl" label={i18n.t("userModal.aiAgent.wizard.fields.schedulingUrl")} helperText={i18n.t("userModal.aiAgent.wizard.fields.schedulingUrlHelp")} variant="outlined" margin="dense" fullWidth />
									<div className={classes.fullField}><Field as={TextField} name="aiAgentProfile.metadata.businessData.advancedOperations" label={i18n.t("userModal.aiAgent.wizard.fields.advancedOperations")} multiline minRows={2} variant="outlined" margin="dense" fullWidth /></div>
									<div className={classes.fullField}><Field as={TextField} name="aiAgentProfile.metadata.businessData.additionalRules" label={i18n.t("userModal.aiAgent.wizard.fields.additionalRules")} helperText={i18n.t("userModal.aiAgent.wizard.fields.additionalRulesHelp")} multiline minRows={3} variant="outlined" margin="dense" fullWidth /></div>
								</div>
							</AccordionDetails>
						</Accordion>
					</>
				)}

				{activeStep === 2 && selectedTemplate && (
					<>
						<Typography ref={headingRef} tabIndex={-1} variant="subtitle1" className={classes.stepHeading}>{i18n.t("userModal.aiAgent.wizard.capabilitiesTitle")}</Typography>
						<Typography variant="body2" className={classes.stepHelp}>{i18n.t("userModal.aiAgent.wizard.capabilitiesHelp")}</Typography>
						<div className={classes.choiceGrid}>
							{selectedTemplate.capabilities.map(capability => {
								const enabled = selectedCapabilityKeys.includes(capability.key);
								const mayNeedIntegration = capability.category === "advanced" || ["scheduling", "appointment_management", "demo_scheduling"].includes(capability.key);
								return (
									<div key={capability.key} className={`${classes.choice} ${enabled ? classes.choiceSelected : ""}`}>
										<div className={classes.capabilityRow}>
											<div>
												<Typography className={classes.choiceTitle}>{capabilityTitle(capability.titleKey)}</Typography>
												<Typography variant="body2" className={classes.muted}>{capabilityTitle(capability.descriptionKey)}</Typography>
												<div className={classes.chipRow}>
													{capability.defaultEnabled && <Chip size="small" label={i18n.t("userModal.aiAgent.wizard.recommendedBadge")} />}
													{mayNeedIntegration && <Chip size="small" variant="outlined" label={i18n.t("userModal.aiAgent.wizard.integrationBadge")} />}
												</div>
											</div>
											<Switch color="primary" checked={enabled} onChange={() => toggleCapability(capability.key)} />
										</div>
									</div>
								);
							})}
						</div>
						<div className={classes.section}>
							<Typography variant="subtitle2" className={classes.sectionTitle}>{i18n.t("userModal.aiAgent.wizard.routingTitle")}</Typography>
							<Typography variant="body2" className={classes.stepHelp}>{i18n.t("userModal.aiAgent.wizard.routingHelp")}</Typography>
							<AiOperationalSkills
								values={values}
								setFieldValue={setFieldValue}
							/>
							<QueueSelect selectedQueueIds={selectedQueueIds} onChange={onQueueIdsChange} label={i18n.t("userModal.aiAgent.wizard.queuesLabel")} />
							{!loadingWhatsApps && (
								<FormControl variant="outlined" margin="dense" fullWidth>
									<InputLabel>{i18n.t("userModal.form.whatsapp")}</InputLabel>
									<Select value={whatsappId || ""} onChange={event => onWhatsappIdChange(event.target.value)} label={i18n.t("userModal.form.whatsapp")}>
										<MenuItem value="">{i18n.t("userModal.aiAgent.wizard.selectConnection")}</MenuItem>
										{whatsApps.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
									</Select>
								</FormControl>
							)}
							<FormControl variant="outlined" margin="dense" fullWidth>
								<InputLabel>{i18n.t("userModal.aiAgent.loopHandoffQueue")}</InputLabel>
								<Select value={values.aiAgentProfile?.loopHandoffQueueId || ""} onChange={event => setFieldValue("aiAgentProfile.loopHandoffQueueId", event.target.value)} label={i18n.t("userModal.aiAgent.loopHandoffQueue")}>
									<MenuItem value="">{i18n.t("userModal.aiAgent.wizard.fields.noHandoffQueue")}</MenuItem>
									{queues.map(queue => <MenuItem key={queue.id} value={queue.id}>{queue.name}</MenuItem>)}
								</Select>
							</FormControl>
							{!readiness.stepReady[2] && <Typography variant="body2" color="error">{i18n.t("userModal.aiAgent.wizard.routingRequired")}</Typography>}
							{requiresScheduling && (
								<TextField
									value={businessData.schedulingUrl || ""}
									onChange={event => setFieldValue("aiAgentProfile.metadata.businessData.schedulingUrl", event.target.value)}
									label={i18n.t("userModal.aiAgent.wizard.fields.schedulingUrl")}
									helperText={i18n.t("userModal.aiAgent.wizard.fields.schedulingUrlRequiredHelp")}
									error={!readiness.checks.scheduling}
									variant="outlined"
									margin="dense"
									fullWidth
								/>
							)}
							{requiresAdvancedOperations && (
								<TextField
									value={businessData.advancedOperations || ""}
									onChange={event => setFieldValue("aiAgentProfile.metadata.businessData.advancedOperations", event.target.value)}
									label={i18n.t("userModal.aiAgent.wizard.fields.advancedOperations")}
									helperText={i18n.t("userModal.aiAgent.wizard.fields.advancedOperationsRequiredHelp")}
									error={!readiness.checks.operations}
									multiline
									minRows={2}
									variant="outlined"
									margin="dense"
									fullWidth
								/>
							)}
						</div>
					</>
				)}

				{activeStep === 3 && (
					<>
						<Typography ref={headingRef} tabIndex={-1} variant="subtitle1" className={classes.stepHeading}>{i18n.t("userModal.aiAgent.wizard.behaviorTitle")}</Typography>
						<Typography variant="body2" className={classes.stepHelp}>{i18n.t("userModal.aiAgent.wizard.behaviorHelp")}</Typography>
						{["intelligenceProfile", "tone"].map(field => (
							<div key={field} className={classes.section}>
								<Typography variant="subtitle2" className={classes.sectionTitle}>{i18n.t(`userModal.aiAgent.wizard.behavior.${field}.title`)}</Typography>
								<Typography variant="body2" className={classes.stepHelp}>{i18n.t(`userModal.aiAgent.wizard.behavior.${field}.help`)}</Typography>
								{renderOptions(field)}
							</div>
						))}
						<Accordion className={classes.optional}>
							<AccordionSummary expandIcon={<ExpandMore />}>
								<div><Typography className={classes.choiceTitle}>{i18n.t("userModal.aiAgent.wizard.optionalBehaviorTitle")}</Typography><Typography variant="body2" className={classes.muted}>{i18n.t("userModal.aiAgent.wizard.optionalBehaviorHelp")}</Typography></div>
							</AccordionSummary>
							<AccordionDetails className={classes.optionalDetails}>
								{["responseLength", "autonomyLevel"].map(field => (
									<div key={field} className={classes.section}>
										<Typography variant="subtitle2" className={classes.sectionTitle}>{i18n.t(`userModal.aiAgent.wizard.behavior.${field}.title`)}</Typography>
										<Typography variant="body2" className={classes.stepHelp}>{i18n.t(`userModal.aiAgent.wizard.behavior.${field}.help`)}</Typography>
										{renderOptions(field)}
									</div>
								))}
							</AccordionDetails>
						</Accordion>
						<div className={classes.usageCard}>
							<Typography variant="subtitle2" className={classes.sectionTitle}>{i18n.t("userModal.aiAgent.wizard.usage.title")}</Typography>
							<Typography variant="body2" className={classes.muted}>
								{i18n.t("userModal.aiAgent.wizard.usage.summary", {
									tokens: usageEstimate.maxOutputTokens,
									cost: i18n.t(`userModal.aiAgent.wizard.usage.cost.${usageEstimate.relativeCost}`),
								})}
							</Typography>
							<Typography variant="caption" className={classes.muted}>{i18n.t("userModal.aiAgent.wizard.usage.help")}</Typography>
						</div>
					</>
				)}

				{activeStep === 4 && (
					<>
						<Typography ref={headingRef} tabIndex={-1} variant="subtitle1" className={classes.stepHeading}>{i18n.t("userModal.aiAgent.wizard.finalReviewTitle")}</Typography>
						<Typography variant="body2" className={classes.stepHelp}>{i18n.t("userModal.aiAgent.wizard.finalReviewHelp")}</Typography>
						<div className={classes.readinessGrid}>
							{readinessEntries.map(([key, ready]) => (
								<div className={classes.readinessItem} key={key}>
									{ready ? <CheckCircleOutline className={classes.readyIcon} /> : <ErrorOutline className={classes.missingIcon} />}
									<div>
										<Typography variant="body2" className={classes.choiceTitle}>{i18n.t(`userModal.aiAgent.wizard.readiness.${key}`)}</Typography>
										<Typography variant="caption" className={classes.muted}>{i18n.t(`userModal.aiAgent.wizard.readiness.${ready ? "ready" : "missing"}`)}</Typography>
									</div>
								</div>
							))}
						</div>
						<div className={classes.reviewCard}>
							{[
								[i18n.t("userModal.aiAgent.wizard.review.agent"), values.name],
								[i18n.t("userModal.aiAgent.wizard.review.segment"), selectedTemplate ? title(selectedTemplate.titleKey) : "—"],
								[i18n.t("userModal.aiAgent.wizard.review.capabilities"), capabilityNames || "—"],
								[i18n.t("userModal.aiAgent.wizard.review.queues"), selectedQueueNames || "—"],
								[i18n.t("userModal.aiAgent.wizard.review.connection"), selectedWhatsapp?.name || "—"],
								[i18n.t("userModal.aiAgent.wizard.review.behavior"), `${i18n.t(`userModal.aiAgent.wizard.behavior.intelligenceProfile.${metadata.intelligenceProfile}`)} · ${i18n.t(`userModal.aiAgent.wizard.behavior.tone.${metadata.tone}`)}`],
								[i18n.t("userModal.aiAgent.wizard.review.usage"), i18n.t("userModal.aiAgent.wizard.usage.summary", { tokens: usageEstimate.maxOutputTokens, cost: i18n.t(`userModal.aiAgent.wizard.usage.cost.${usageEstimate.relativeCost}`) })],
							].map(([label, value]) => (
								<div className={classes.reviewLine} key={label}>
									<Typography variant="body2" className={classes.muted}>{label}</Typography>
									<Typography variant="body2" className={classes.reviewValue}>{value}</Typography>
								</div>
							))}
						</div>
						<div className={classes.reviewCard}>
							<Typography variant="subtitle2" className={classes.sectionTitle}>{i18n.t("userModal.aiAgent.wizard.simulator.title")}</Typography>
							<Typography variant="body2" className={classes.muted}>{i18n.t("userModal.aiAgent.wizard.simulator.help")}</Typography>
							<TextField
								value={simulatorMessage}
								onChange={event => setSimulatorMessage(event.target.value)}
								label={i18n.t("userModal.aiAgent.wizard.simulator.message")}
								placeholder={i18n.t("userModal.aiAgent.wizard.simulator.placeholder")}
								variant="outlined"
								margin="dense"
								fullWidth
								multiline
								minRows={2}
								inputProps={{ maxLength: 1500 }}
							/>
							<div className={classes.simulatorActions}>
								<Button
									type="button"
									color="primary"
									variant="outlined"
									disabled={!userId || simulating || !readiness.isReady || String(simulatorMessage || "").trim().length < 2}
									onClick={runSimulation}
								>
									{simulating ? i18n.t("userModal.aiAgent.wizard.simulator.running") : i18n.t("userModal.aiAgent.wizard.simulator.run")}
								</Button>
								{!userId && <Typography variant="caption" className={classes.muted}>{i18n.t("userModal.aiAgent.wizard.simulator.saveFirst")}</Typography>}
							</div>
							{simulatorAnswer && <Typography variant="body2" className={classes.simulatorAnswer}>{simulatorAnswer}</Typography>}
							<Typography variant="caption" className={classes.muted}>{i18n.t("userModal.aiAgent.wizard.simulator.operationsNote")}</Typography>
						</div>
						<div className={classes.reviewCard}>
							<Typography variant="subtitle2" className={classes.sectionTitle}>{i18n.t("userModal.aiAgent.wizard.testTitle")}</Typography>
							<Typography variant="body2" className={classes.muted}>{i18n.t("userModal.aiAgent.wizard.testHelp")}</Typography>
							<ul className={classes.testList}>
								{["knownQuestion", "unknownQuestion", "humanRequest"].map(item => <li key={item}><Typography variant="body2">{i18n.t(`userModal.aiAgent.wizard.test.${item}`)}</Typography></li>)}
							</ul>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default AiAgentWizard;
